import {
  getFlightSegmentsWithLegs,
  mapRawLdsSupplementsToMockupExtras,
  normalizeAvailableFlights,
} from "../adapters/flightOfferAdapter";
import { resolveAgentFlightDisplayedSortPrice } from "./resolveAgentFlightDisplayedSortPrice";

/** @typedef {'all'|'rt'|'ow_out'|'ow_in'} FlightRouteTypeFilter */
/** @typedef {'cheapest'|'fastest'|'recommended'|'earliest_out'|'latest_out'|'fewest_stops'} FlightResultsSortMode */
/** @typedef {'direct'|'one'|'two_plus'} StopsBand */
/** @typedef {'morning'|'afternoon'|'evening'|'night'} TimeOfDayBucket */

/**
 * @param {object} gate
 * @param {unknown} roundTripFlightResults
 * @param {unknown} outboundOneWayFlightResults
 * @param {unknown} inboundOneWayFlightResults
 * @param {unknown} availableFlights
 * @returns {{ deal: object, routeKind: FlightRouteTypeFilter | null }[]}
 */
export function buildTaggedDealsForResults({
  gate,
  roundTripFlightResults,
  outboundOneWayFlightResults,
  inboundOneWayFlightResults,
  availableFlights,
}) {
  const norm = normalizeAvailableFlights;
  const rtDeals = norm(roundTripFlightResults);
  const available = norm(availableFlights);

  /* Always prefer ready-made RT deals when they exist — even if parallelOk is
   * still false or the self-compose gate is on. Returning [] here is what
   * paints "אין תוצאות עדיין" on /flights/results after a real RT search. */
  if (rtDeals.length > 0) {
    return rtDeals.map((d) => ({
      deal: d,
      routeKind: /** @type {const} */ ("rt"),
    }));
  }

  if (!gate.parallelOk) {
    return available.map((d) => ({ deal: d, routeKind: null }));
  }

  if (
    gate.needsSelfComposePickStep ||
    (gate.partialSelfCompose && !gate.outboundOnlyNoReturn)
  ) {
    if (available.length > 0) {
      return available.map((d) => ({ deal: d, routeKind: null }));
    }
    return [];
  }

  /* חיפוש הלוך-חזור מקבילי: ברשימת התוצאות רק עסקאות RT. תוצאות OW לא ממוזגות —
   * הרכבה מנפרד (הלוך+חזור) נשארת במסכים ייעודיים (למשל selfComposePick / פאנל סוכן). */
  if (gate.outboundOnlyNoReturn) {
    return norm(outboundOneWayFlightResults).map((d) => ({
      deal: d,
      routeKind: /** @type {const} */ ("ow_out"),
    }));
  }

  return available.map((d) => ({ deal: d, routeKind: null }));
}

function depHourFromLegDate(dateStr) {
  if (dateStr == null || dateStr === "") return null;
  const s = String(dateStr).trim();
  const iso = s.match(/T(\d{1,2})/);
  if (iso) return parseInt(iso[1], 10);
  const parts = s.split(/\s+/);
  if (parts.length >= 2) {
    const hm = parts[1].match(/^(\d{1,2}):/);
    if (hm) return parseInt(hm[1], 10);
  }
  return null;
}

/**
 * @param {number|null|undefined} h
 * @returns {TimeOfDayBucket|null}
 */
export function hourToTimeBucket(h) {
  if (h == null || !Number.isFinite(h)) return null;
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
}

function positiveNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * @param {object} deal
 * @param {object} offer — adaptDealToFlightOffer + parallelRouteKind
 */
export function extractFlightOfferTraits(deal, offer) {
  const segLegs = getFlightSegmentsWithLegs(deal);
  let totalConnections = 0;
  let totalDur = 0;
  /** @type {number|null} */
  let outDepHour = null;
  /** @type {number|null} */
  let retDepHour = null;
  const airlines = new Set();
  const depAirports = new Set();
  const arrAirports = new Set();

  segLegs.forEach((sl, idx) => {
    const legs = sl.legs;
    totalConnections += Math.max(0, legs.length - 1);
    for (const L of legs) {
      const d = Number(L?.duration);
      if (Number.isFinite(d) && d > 0) totalDur += d;
      const ac = L?.airlineId || L?.airlineCode;
      if (ac != null && String(ac).trim() !== "")
        airlines.add(String(ac).trim());
    }
    const first = legs[0];
    const last = legs[legs.length - 1];
    if (first) {
      const dep = first.depAirport?.code || first.depCity;
      if (dep != null && String(dep).trim() !== "")
        depAirports.add(String(dep).trim());
    }
    if (last) {
      const arr = last.arrAirport?.code || last.arrCity;
      if (arr != null && String(arr).trim() !== "")
        arrAirports.add(String(arr).trim());
    }
    const h = depHourFromLegDate(first?.depDate);
    if (idx === 0) outDepHour = h;
    if (idx === 1) retDepHour = h;
  });

  const bagRows = mapRawLdsSupplementsToMockupExtras(deal);
  let hasTrolley = false;
  let hasChecked = false;
  for (const r of bagRows) {
    if (r.type === "TROLLEY") hasTrolley = true;
    if (r.type === "LUGGAGE" || r.type === "BAGGAGE") hasChecked = true;
  }

  /** @type {'personal_only'|'trolley'|'checked'|'trolley_and_checked'|'unknown'} */
  let baggageProfile = "unknown";
  if (bagRows.length === 0) baggageProfile = "unknown";
  else if (hasChecked && hasTrolley) baggageProfile = "trolley_and_checked";
  else if (hasChecked) baggageProfile = "checked";
  else if (hasTrolley) baggageProfile = "trolley";
  else baggageProfile = "personal_only";

  /** @type {StopsBand} */
  const stopsBand =
    totalConnections <= 0
      ? "direct"
      : totalConnections === 1
        ? "one"
        : "two_plus";

  const ppNum = positiveNumber(offer?.pricePerPerson);
  const tfNum = positiveNumber(offer?.totalFare);

  return {
    totalConnections,
    stopsBand,
    totalDurationMin: totalDur > 0 ? totalDur : null,
    outDepHour,
    retDepHour,
    outBucket: hourToTimeBucket(outDepHour),
    retBucket: hourToTimeBucket(retDepHour),
    airlines: [...airlines].sort(),
    depAirports: [...depAirports].sort(),
    arrAirports: [...arrAirports].sort(),
    baggageProfile,
    hasTrolley,
    hasChecked,
    baggageRowCount: bagRows.length,
    pricePerPersonNum: ppNum,
    totalFareNum: tfNum,
    parallelRouteKind: offer?.parallelRouteKind ?? null,
    numSegments: segLegs.length,
  };
}

export function defaultFlightResultsFilterState() {
  return {
    routeType: /** @type {FlightRouteTypeFilter} */ ("all"),
    pricePerPersonRange: null,
    totalFareRange: null,
    useTotalPriceForRange: false,
    stops: /** @type {StopsBand|null} */ (null),
    airlines: /** @type {string[]} */ ([]),
    baggage: /** @type {'personal_only'|'trolley'|'checked'|'unknown'|null} */ (null),
    durationRangeMin: null,
    depAirports: /** @type {string[]} */ ([]),
    arrAirports: /** @type {string[]} */ ([]),
    timeOutbound: /** @type {TimeOfDayBucket|null} */ (null),
    timeReturn: /** @type {TimeOfDayBucket|null} */ (null),
  };
}

/**
 * @param {object} traits
 * @param {ReturnType<typeof defaultFlightResultsFilterState>} f
 */
export function flightOfferMatchesFilters(traits, f) {
  if (f.routeType !== "all") {
    if (traits.parallelRouteKind == null) return false;
    if (f.routeType !== traits.parallelRouteKind) return false;
  }

  const useTotal = f.useTotalPriceForRange;
  const range = useTotal ? f.totalFareRange : f.pricePerPersonRange;
  if (range && range.length === 2) {
    const [lo, hi] = range;
    const v = useTotal ? traits.totalFareNum : traits.pricePerPersonNum;
    if (v == null) return false;
    if (v < lo || v > hi) return false;
  }

  if (f.stops != null && traits.stopsBand !== f.stops) return false;

  if (f.airlines.length > 0) {
    const hit = f.airlines.some((a) => traits.airlines.includes(a));
    if (!hit) return false;
  }

  if (f.baggage != null) {
    if (f.baggage === "personal_only") {
      if (traits.hasTrolley || traits.hasChecked) return false;
    } else if (f.baggage === "unknown") {
      if (traits.baggageRowCount > 0) return false;
    } else if (f.baggage === "trolley") {
      if (!traits.hasTrolley) return false;
    } else if (f.baggage === "checked") {
      if (!traits.hasChecked) return false;
    }
  }

  if (f.durationRangeMin && f.durationRangeMin.length === 2) {
    const [lo, hi] = f.durationRangeMin;
    if (traits.totalDurationMin == null) return false;
    if (traits.totalDurationMin < lo || traits.totalDurationMin > hi)
      return false;
  }

  if (f.depAirports.length > 0) {
    const hit = f.depAirports.some((c) => traits.depAirports.includes(c));
    if (!hit) return false;
  }
  if (f.arrAirports.length > 0) {
    const hit = f.arrAirports.some((c) => traits.arrAirports.includes(c));
    if (!hit) return false;
  }

  if (f.timeOutbound != null) {
    if (traits.outBucket !== f.timeOutbound) return false;
  }
  if (f.timeReturn != null) {
    if (traits.numSegments < 2) return false;
    if (traits.retBucket !== f.timeReturn) return false;
  }

  return true;
}

function sortKeyPrice(traits) {
  return traits.pricePerPersonNum ?? traits.totalFareNum ?? Number.POSITIVE_INFINITY;
}

function sortKeyDuration(traits) {
  return traits.totalDurationMin ?? Number.POSITIVE_INFINITY;
}

function sortKeyStops(traits) {
  return traits.totalConnections;
}

function sortKeyEarliestOut(traits) {
  if (traits.outDepHour == null) return 999;
  return traits.outDepHour * 60;
}

function isDevRuntime() {
  const viteDev =
    typeof import.meta !== "undefined" && import.meta.env?.DEV === true;
  const nodeDev = process.env.NODE_ENV === "development";
  return viteDev || nodeDev;
}

function resolveOfferSupplierLabel(offer) {
  const fromCardMeta = offer?.cardMeta?.supplierCode;
  if (fromCardMeta != null && String(fromCardMeta).trim() !== "") {
    return String(fromCardMeta).trim();
  }
  const raw = offer?.rawDeal;
  const fromDeal =
    raw?.supplierDetails?.supplierId ??
    raw?.supplierDetails?.SupplierId ??
    raw?.supplierdetails?.supplierId ??
    raw?.supplier ??
    null;
  if (fromDeal != null && String(fromDeal).trim() !== "") {
    return String(fromDeal).trim();
  }
  return null;
}

/**
 * @param {object[]} offers
 * @param {Map<string, object>} traitsByOfferId
 * @param {FlightResultsSortMode} mode
 */
export function sortFlightOffers(offers, traitsByOfferId, mode) {
  if (!Array.isArray(offers) || offers.length <= 1) return [...offers];
  const dev = isDevRuntime();
  const scored = offers.map((o) => {
    const t = traitsByOfferId.get(String(o.id)) || {};
    const price = sortKeyPrice(t);
    const displayedSort = resolveAgentFlightDisplayedSortPrice(o);
    const cheapestSortPrice =
      displayedSort.sortPrice != null
        ? displayedSort.sortPrice
        : Number.POSITIVE_INFINITY;
    if (mode === "cheapest" && !Number.isFinite(displayedSort.sortPrice) && dev) {
      console.warn("[AGENT_FLIGHT_SORT_PRICE_MISSING]", {
        sortMode: mode,
        flightId: o?.id ?? null,
        supplier: resolveOfferSupplierLabel(o),
        sortPrice: displayedSort.sortPrice,
        sortPriceSource: displayedSort.sortPriceSource,
        cardPriceSource: displayedSort.cardPriceSource,
        displayedUsd: displayedSort.displayedUsd,
        displayedIls: displayedSort.displayedIls,
        displayedTotalIls: displayedSort.displayedTotalIls,
      });
    }
    const dur = sortKeyDuration(t);
    const stops = sortKeyStops(t);
    const earliest = sortKeyEarliestOut(t);
    const latestHour = t.outDepHour != null ? t.outDepHour : -1;
    let rec = 0;
    if (Number.isFinite(price) && price < Number.POSITIVE_INFINITY) {
      rec += price * 0.65;
    }
    if (dur < Number.POSITIVE_INFINITY) rec += dur * 0.25;
    rec += stops * 120 * 0.1;
    return {
      o,
      price,
      cheapestSortPrice,
      cheapestSortPriceSource: displayedSort.sortPriceSource,
      cardPriceSource: displayedSort.cardPriceSource,
      displayedUsd: displayedSort.displayedUsd,
      displayedIls: displayedSort.displayedIls,
      displayedTotalIls: displayedSort.displayedTotalIls,
      dur,
      stops,
      earliest,
      latestHour,
      rec,
    };
  });

  const cmpId = (a, b) => String(a.o.id).localeCompare(String(b.o.id));

  if (mode === "cheapest") {
    scored.sort((a, b) => {
      const d = a.cheapestSortPrice - b.cheapestSortPrice;
      if (d !== 0) return d;
      return cmpId(a, b);
    });
  } else if (mode === "fastest") {
    scored.sort((a, b) => {
      const d = a.dur - b.dur;
      if (d !== 0) return d;
      const p = a.price - b.price;
      if (p !== 0) return p;
      return cmpId(a, b);
    });
  } else if (mode === "fewest_stops") {
    scored.sort((a, b) => {
      const d = a.stops - b.stops;
      if (d !== 0) return d;
      const p = a.price - b.price;
      if (p !== 0) return p;
      return cmpId(a, b);
    });
  } else if (mode === "earliest_out") {
    scored.sort((a, b) => {
      const d = a.earliest - b.earliest;
      if (d !== 0) return d;
      return cmpId(a, b);
    });
  } else if (mode === "latest_out") {
    scored.sort((a, b) => {
      const d = b.latestHour - a.latestHour;
      if (d !== 0) return d;
      return cmpId(a, b);
    });
  } else if (mode === "recommended") {
    scored.sort((a, b) => {
      const d = a.rec - b.rec;
      if (d !== 0) return d;
      return cmpId(a, b);
    });
  } else {
    scored.sort((a, b) => {
      const d = a.price - b.price;
      if (d !== 0) return d;
      return cmpId(a, b);
    });
  }

  if (dev) {
    scored.forEach((row, indexAfterSort) => {
      console.log("[AGENT_FLIGHTS_SORT_AUDIT]", {
        sortMode: mode,
        flightId: row?.o?.id ?? null,
        supplier: resolveOfferSupplierLabel(row?.o),
        displayedUsd: row.displayedUsd,
        displayedIls: row.displayedIls,
        sortPrice:
          mode === "cheapest" ? row.cheapestSortPrice : row.price,
        sortPriceSource:
          mode === "cheapest"
            ? row.cheapestSortPriceSource
            : "traits.pricePerPersonNum_or_totalFareNum",
        cardPriceSource: row.cardPriceSource,
        indexAfterSort,
      });
    });
  }

  return scored.map((x) => x.o);
}

/**
 * מחירון וטווחי משך מהצעות (לפני פילטר מחיר/משך) — לסליידרים.
 * @param {object[]} offers
 * @param {Map<string, object>} traitsByOfferId
 */
export function computePriceAndDurationBounds(offers, traitsByOfferId) {
  let ppMin = Infinity;
  let ppMax = -Infinity;
  let tfMin = Infinity;
  let tfMax = -Infinity;
  let durMin = Infinity;
  let durMax = -Infinity;
  for (const o of offers) {
    const t = traitsByOfferId.get(String(o.id));
    if (!t) continue;
    if (t.pricePerPersonNum != null) {
      ppMin = Math.min(ppMin, t.pricePerPersonNum);
      ppMax = Math.max(ppMax, t.pricePerPersonNum);
    }
    if (t.totalFareNum != null) {
      tfMin = Math.min(tfMin, t.totalFareNum);
      tfMax = Math.max(tfMax, t.totalFareNum);
    }
    if (t.totalDurationMin != null) {
      durMin = Math.min(durMin, t.totalDurationMin);
      durMax = Math.max(durMax, t.totalDurationMin);
    }
  }
  const noPp = !Number.isFinite(ppMin) || !Number.isFinite(ppMax);
  const noTf = !Number.isFinite(tfMin) || !Number.isFinite(tfMax);
  const noDur = !Number.isFinite(durMin) || !Number.isFinite(durMax);
  return {
    pricePerPerson: noPp ? null : [ppMin, ppMax],
    totalFare: noTf ? null : [tfMin, tfMax],
    durationMin: noDur ? null : [durMin, durMax],
  };
}

export function countOffersByRouteKind(taggedDeals) {
  let rt = 0;
  let owOut = 0;
  let owIn = 0;
  for (const row of taggedDeals) {
    if (row.routeKind === "rt") rt += 1;
    else if (row.routeKind === "ow_out") owOut += 1;
    else if (row.routeKind === "ow_in") owIn += 1;
  }
  return { rt, owOut, owIn, total: taggedDeals.length };
}
